import { buildCanonicalUrl, SITE_ORIGIN } from "./seo.js";
import { getBlogPath } from "./blogUrl.js";
import { blogAssetUrl } from "./blogApi.js";

// Single source of truth for blog sharing (desktop icon row, mobile share
// sheet) and for the blog's social-preview image, so every surface uses the
// same canonical URL, title and image.
//
// Pure string functions on purpose (no DOMParser/window): they run the same
// in the browser, in Server Components and in `node --test`.

const FALLBACK_IMAGE = `${SITE_ORIGIN}/Hachion-logo.png`;

// Absolute public URL of the blog's own featured image (blog.blog_image is a
// bare path from the `blogs` table, e.g. "images/x.webp"); see lib/blogApi.js
// for the environment-driven base and the percent-encoding. No per-blog image
// => site logo.
export function getBlogImageUrl(blog) {
  return blogAssetUrl(blog?.blog_image) || FALLBACK_IMAGE;
}

// Image URL advertised to social platforms and used in shared emails: the
// blog's own image re-encoded as a 1200x630 JPEG by /blog-og/[id] (the whole
// original is kept, letterboxed - never cropped). Unlike the stored files
// (WebP, arbitrary sizes) this is accepted by every platform and mail client.
// Same-origin path with no query string, because robots.txt disallows both
// /api/ and any URL containing "?". Blogs without an image get the site logo.
export const SOCIAL_IMAGE_WIDTH = 1200;
export const SOCIAL_IMAGE_HEIGHT = 630;
export function getBlogSocialImageUrl(blog) {
  const id = Number(blog?.id);
  if (!Number.isInteger(id) || id <= 0 || !blogAssetUrl(blog?.blog_image)) return FALLBACK_IMAGE;
  return `${SITE_ORIGIN}/blog-og/${id}`;
}

// Canonical (lowercase, no query/trailing slash) absolute URL for sharing.
// Derived from the blog's own slug, not window.location, so tracking
// parameters or a non-canonical entry URL are never what gets shared.
export function getBlogShareUrl(blog, fallbackPath = "") {
  return buildCanonicalUrl(getBlogPath(blog) || fallbackPath || "/blogs");
}

const NAMED_ENTITIES = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ", ndash: "–", mdash: "—", hellip: "…", rsquo: "’", lsquo: "‘", ldquo: "“", rdquo: "”" };

function decodeEntities(text) {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, body) => {
    if (body[0] === "#") {
      const code = body[1].toLowerCase() === "x" ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      try {
        return Number.isFinite(code) ? String.fromCodePoint(code) : match;
      } catch {
        return match;
      }
    }
    return NAMED_ENTITIES[body.toLowerCase()] ?? match;
  });
}

// First meaningful paragraph of the blog's HTML body, as plain text: the
// first <p> (outside script/style/nav/aside/figure/header/footer/form/table/
// list blocks) whose text is at least `minLength` characters, which skips
// empty spacer paragraphs, image-only paragraphs and one-line labels such as
// "Introduction". Headings are never <p>, so they are excluded by
// construction. Returns "" when nothing qualifies.
export function extractFirstParagraph(html, { minLength = 40, maxLength = 500 } = {}) {
  if (typeof html !== "string" || !html) return "";
  const cleaned = html
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<(script|style|nav|aside|figure|header|footer|form|table|ul|ol|noscript|iframe|svg)\b[\s\S]*?<\/\1\s*>/gi, " ");

  const paragraphRe = /<p\b[^>]*>([\s\S]*?)<\/p\s*>/gi;
  let match;
  while ((match = paragraphRe.exec(cleaned))) {
    const text = decodeEntities(match[1].replace(/<br\s*\/?>/gi, " ").replace(/<[^>]+>/g, " "))
      .replace(/\s+/g, " ")
      .trim();
    if (text.length < minLength) continue;
    if (text.length <= maxLength) return text;
    const cut = text.slice(0, maxLength);
    const lastSpace = cut.lastIndexOf(" ");
    return `${cut.slice(0, lastSpace > maxLength * 0.6 ? lastSpace : maxLength).trimEnd()}…`;
  }
  return "";
}

// Email subject/body for the "share by email" action. The body leads with the
// blog's actual first paragraph; if the content has none (or is unavailable)
// it falls back to the meta description, then to a plain one-line intro.
// The plain-text body never contains the image URL: the picture reaches the
// recipient through the HTML card (see buildEmailHtml) or the link preview.
export function buildEmailShare({ title, html, metaDescription, url }) {
  const subject = title || "Hachion Blog";
  const paragraph = extractFirstParagraph(html) || (metaDescription || "").replace(/\s+/g, " ").trim();
  const body = paragraph ? paragraph + "\n\nRead the full article: " + url : "I thought you might like this blog: \"" + subject + "\"\n\n" + url;
  return { subject, body };
}

const escapeHtml = (v) => String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// The email "card": clickable blog image, title, first paragraph and link, as
// self-contained inline-styled HTML with an absolute public image URL (no
// scripts/classes, so Gmail keeps it). A Gmail compose URL can only carry plain
// text, so the share button puts this on the clipboard for pasting into the
// message body, where Gmail renders the image.
export function buildEmailHtml({ title, html, metaDescription, url, imageUrl }) {
  const paragraph = extractFirstParagraph(html) || (metaDescription || "").replace(/\s+/g, " ").trim();
  const link = escapeHtml(url);
  const heading = escapeHtml(title || "Hachion Blog");
  return [
    '<div style="font-family:Arial,Helvetica,sans-serif;max-width:600px">',
    imageUrl ? '<a href="' + link + '" target="_blank"><img src="' + escapeHtml(imageUrl) + '" alt="' + heading + '" width="600" style="display:block;width:100%;max-width:600px;height:auto;border:0"></a>' : "",
    '<h2 style="margin:12px 0 8px;font-size:20px"><a href="' + link + '" target="_blank" style="color:#1a1a1a;text-decoration:none">' + heading + "</a></h2>",
    paragraph ? '<p style="margin:0 0 12px;font-size:15px;line-height:1.5;color:#333">' + escapeHtml(paragraph) + "</p>" : "",
    '<p style="margin:0"><a href="' + link + '" target="_blank">Read the full article</a></p>',
    "</div>",
  ].join("");
}

// The five platform links. `gmail` keeps the existing Gmail-compose behaviour
// of the email icon; `mailto` is provided for callers that prefer the OS
// mail handler.
export function buildShareLinks({ url, title, html, metaDescription }) {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);
  const { subject, body } = buildEmailShare({ title, html, metaDescription, url });
  const s = encodeURIComponent(subject);
  const b = encodeURIComponent(body);
  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    twitter: `https://twitter.com/intent/tweet?url=${u}&text=${t}&via=hachionofficial`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
    email: `https://mail.google.com/mail/?view=cm&fs=1&to=&su=${s}&body=${b}`,
    mailto: `mailto:?subject=${s}&body=${b}`,
  };
}

// Puts the email card on the clipboard as rich HTML (plus a plain-text twin) so
// pasting into Gmail's compose box inserts the clickable image. Must be called
// directly from the click handler (user gesture). Resolves false when the
// browser has no async clipboard / ClipboardItem support or denies it, in which
// case the plain-text body (which carries the link and the image URL) is all
// the recipient gets.
export async function copyEmailCard({ html, text }) {
  try {
    if (typeof ClipboardItem === "undefined" || !navigator?.clipboard?.write) return false;
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([text], { type: "text/plain" }),
      }),
    ]);
    return true;
  } catch {
    return false;
  }
}

// ---- Sharing the actual image file (Web Share API level 2) ----
//
// Facebook / X / LinkedIn / WhatsApp *web* share links can only carry a URL;
// those platforms build the picture themselves from the page's Open Graph tags.
// A file can only be attached through the browser's native share sheet, and only
// where navigator.canShare({ files }) says so. Callers must hide the option when
// canShareImageFile() is false rather than pretend an image was attached.

// Same-origin path of the blog's 1200x630 JPEG (fetchable without CORS), or ""
// when the blog has no image of its own.
export function getBlogSocialImagePath(blog) {
  const id = Number(blog?.id);
  if (!Number.isInteger(id) || id <= 0 || !blogAssetUrl(blog?.blog_image)) return "";
  return "/blog-og/" + id;
}

export async function fetchShareImageFile(path, name = "blog-image") {
  const res = await fetch(path);
  const type = (res.headers.get("content-type") || "").split(";")[0].trim();
  if (!res.ok || !type.startsWith("image/")) throw new Error("image unavailable");
  const blob = await res.blob();
  const ext = type === "image/png" ? "png" : "jpg";
  const base = String(name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "blog-image";
  return new File([blob], base + "." + ext, { type });
}

export function canShareImageFile(file) {
  try {
    return Boolean(file) && typeof navigator !== "undefined" && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
}

// Opens the native share sheet with the image attached. The link is in the text
// (targets that receive a file usually drop a separate url field). Must run
// inside a click handler; the file is fetched beforehand so the gesture is kept.
// Resolves "shared" | "cancelled"; throws on a real failure.
export async function shareBlogWithImage({ file, title, description, url }) {
  const text = [title, description, url].filter(Boolean).join("\n\n");
  try {
    await navigator.share({ files: [file], title, text });
    return "shared";
  } catch (err) {
    if (err && err.name === "AbortError") return "cancelled";
    throw err;
  }
}
